// ============================================
// gsap scrollTrigger
// ============================================
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);
import Lenis from 'lenis';

// ============================================
// Lenis
// ============================================
export let lenis = new Lenis({
  lerp: 0.12,
  duration: 1.4,
  smoothWheel: true,
  smoothTouch: false,
});

//初期設定
lenis.on('scroll', ScrollTrigger.update);
const _raf = (time) => lenis.raf(time * 1000);
gsap.ticker.add(_raf);
gsap.ticker.lagSmoothing(1000, 16);

const REINIT_MS = 2 * 60 * 1000;
setInterval(() => {
  gsap.ticker.remove(_raf);
  lenis.destroy();

  lenis = new Lenis({
    lerp: 0.1,
    duration: 1.0,
    smoothWheel: true,
    smoothTouch: true,
  });

  lenis.on('scroll', () => ScrollTrigger.update());
  gsap.ticker.add(_raf);
  ScrollTrigger.refresh();
}, REINIT_MS);

// ============================================
// ページ内リンク
// ============================================
let anchorsBound = false;
function lenisPageScroll() {
  const selector = 'a[href^="#"]:not(.-no-smooth, .l-header__skip-content)';
  if (!document.querySelector(selector)) return;
  if (anchorsBound) return;
  anchorsBound = true;

  let headerOffset = 0;
  const root = document.documentElement;

  const updateHeaderOffset = () => {
    headerOffset = -1 * parseFloat(getComputedStyle(root).getPropertyValue('--size-header-h')) || 0;
  };

  const handleClick = (event) => {
    const target = event.target.closest(selector);
    if (!target) return;

    const href = target.getAttribute('href');
    if (!href) return;

    event.preventDefault();
    updateHeaderOffset();

    lenis.scrollTo(href === '#' ? 'body' : href, {
      offset: headerOffset,
      lerp: 0.2,
      duration: 1.2,
    });
  };

  updateHeaderOffset();
  document.addEventListener('click', handleClick, { passive: false });
}

// ============================================
// スクロールアニメーション
// ============================================
function inViewAnm() {
  gsap.utils.toArray('.js-inview').forEach((el) => {
    ScrollTrigger.create({
      //markers: true,
      trigger: el,
      start: 'top 75%',
      end: 'bottom 75%',
      once: true,
      toggleClass: {
        targets: el,
        className: 'is-active',
      },
    });
  });
}

// ============================================
// 作品パララックス
// ============================================
function pictureAnim() {
  const articles = document.querySelectorAll('.c-mv-post__post, .js-parallax');
  if (articles.length === 0) return;

  gsap.utils.toArray(articles).forEach((img) => {
    const speed = img.dataset.speed ?? 0;
    gsap.fromTo(
      img,
      { y: speed },
      {
        y: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: img,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1.2,
        },
      }
    );
    ScrollTrigger.create({
      trigger: img,
      start: 'top 75%',
      end: 'bottom 45%',
      toggleClass: { targets: img, className: 'is-active' },
    });
  });
}

// ============================================
// MV黒背景の写真アニメーション
// ============================================
function blackBgAnim() {
  const black = document.querySelector('.p-home-bg-black');
  const images = document.querySelectorAll('.p-home-about__imgWrap .p-home-about__img');

  if (!black || images.length === 0) return;

  let duration, delay;
  if (black.classList.contains('-first')) {
    duration = 1.6;
    delay = 3.4;
  } else {
    duration = 1.0;
    delay = 0.0;
  }

  //写真
  gsap.utils.toArray(images).forEach((img) => {
    const x = img.dataset.x;
    gsap.to(img, {
      x: 0,
      y: 0,
      ease: 'power3.out',
      duration,
      delay,
    });
  });
}

// ============================================
// footerパララックスアニメーション
// ============================================
function footerParallax() {
  const bg = document.querySelector('.c-bg');
  const discord = document.querySelector('.c-discord');
  if (!bg || !discord) return;

  const height = window.innerHeight + 100;

  gsap.fromTo(
    discord,
    {
      y: 400,
    },
    {
      y: 0,
      scrollTrigger: {
        trigger: bg,
        start: () => `bottom-=${height}px top`,
        end: () => 'bottom top',
        scrub: true,
        ease: 'none',
      },
    }
  );
}

// ============================================
// 羽の動くやつ
// ============================================
function mvFeather() {
  const img = document.querySelector('.p-home-artworks__feather-img');
  const path = document.querySelector('.p-home-artworks__feather-line');
  if (!img) return;

  gsap.to(img, {
    motionPath: {
      path: path,
      align: path,
      alignOrigin: [0.5, 0.5],
      start: 0.51,
      end: 1,
    },
    ease: 'none',
    scrollTrigger: {
      trigger: path,
      start: 'top bottom',
      end: 'bottom 50%',
      scrub: 1,
    },
    keyframes: [
      { progress: 0.4, rotate: 30 },
      { progress: 0.6, scale: 1.6 },
      { progress: 0.8, rotate: -20 },
      { progress: 1.0, scale: 1.4 },
      { progress: 1.0, rotate: 0 },
    ],
  });
}

// リサイズ
// ==========================
function debounce(func, delay = 200) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

let lastWidth = window.innerWidth;

window.addEventListener(
  'resize',
  debounce(() => {
    const currentWidth = window.innerWidth;

    // 幅が変わったときだけ ScrollTrigger.refresh()
    if (currentWidth !== lastWidth) {
      ScrollTrigger.refresh();
      lastWidth = currentWidth;
    }
  })
);

export { lenisPageScroll, inViewAnm, pictureAnim, footerParallax, mvFeather, blackBgAnim };
